import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  type EmailChangeDraft,
  emptyDraft,
  type PendingEmailChange,
} from '../domain/email-change.model';
import type { EmailChangeError } from '../domain/email-change-error';
import { initialState } from './email-change.state';

/**
 * Email-change slice. Reducers are pure state transitions only; the HTTP calls
 * live in the sagas, which dispatch these transitions as they run.
 *
 * ## The rule to keep
 *
 * Every path that leaves the form (`closed`, `submitSucceeded`, `reset`) puts
 * `draft` back to `emptyDraft`. The password typed in it must not survive the moment
 * it was needed: the store is not persisted, but it is readable from the devtools
 * and from any code holding the store. If you add a branch, wipe the draft in it too.
 *
 * `pending` is deliberately NOT wiped by `reset`: it is server state, not typed
 * input, and the section reloads it on mount anyway.
 */
const slice = createSlice({
  name: 'emailChange',
  initialState,
  reducers: {
    /** Open the form (from the collapsed "Sign-in email / Change" row). */
    opened: (state) => {
      state.open = true;
      state.draft = emptyDraft;
      state.validationErrors = {};
      state.error = null;
    },
    /** Close without submitting; discards the typed password. */
    closed: (state) => {
      state.open = false;
      state.draft = emptyDraft;
      state.validationErrors = {};
      state.error = null;
      state.submitStatus = 'idle';
    },

    /** A single field changed; clears that field's error so it stops shouting mid-fix. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof EmailChangeDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
      if (state.submitStatus === 'failed') {
        state.submitStatus = 'idle';
        state.error = null;
      }
    },

    // ── Pending-change load lifecycle (dispatched by the saga) ──
    pendingLoadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    pendingLoaded: (state, action: PayloadAction<PendingEmailChange | null>) => {
      state.loadStatus = 'loaded';
      state.pending = action.payload;
    },
    /**
     * A failed load leaves `pending` as it was rather than blanking it. Guessing
     * "nothing is in flight" from a network error would hide a request the owner
     * needs to see, which is exactly the case this section exists for.
     */
    pendingLoadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    // ── Submit lifecycle (dispatched by the saga) ──
    submitStarted: (state) => {
      state.submitStatus = 'submitting';
      state.error = null;
      state.validationErrors = {};
    },
    /**
     * Success closes the form, wipes the password, and puts the pending change on
     * screen. Nothing about the account has changed yet; the banner says so.
     */
    submitSucceeded: (state, action: PayloadAction<PendingEmailChange>) => {
      state.submitStatus = 'succeeded';
      state.open = false;
      state.draft = emptyDraft;
      state.validationErrors = {};
      state.error = null;
      state.pending = action.payload;
      state.loadStatus = 'loaded';
    },
    submitFailed: (state, action: PayloadAction<EmailChangeError>) => {
      state.submitStatus = 'failed';
      state.error = action.payload;
      state.validationErrors =
        action.payload.kind === 'validation' ? action.payload.fields : {};
    },
    /** The section handled the success (notified); back to idle. */
    submitAcknowledged: (state) => {
      state.submitStatus = 'idle';
    },

    // ── Cancel lifecycle (dispatched by the saga) ──
    cancelStarted: (state) => {
      state.cancelling = true;
      state.error = null;
    },
    cancelSucceeded: (state) => {
      state.cancelling = false;
      state.pending = null;
    },
    cancelFailed: (state, action: PayloadAction<EmailChangeError>) => {
      state.cancelling = false;
      // Never a field error: there is no form on screen to attach one to, so the
      // section shows it as a snackbar and the banner stays put.
      state.error = action.payload;
    },

    /** Reset the form (on unmount / navigation away). Keeps the loaded `pending`. */
    reset: (state) => {
      state.open = false;
      state.draft = emptyDraft;
      state.validationErrors = {};
      state.error = null;
      state.submitStatus = 'idle';
      state.cancelling = false;
    },
  },
});

export const emailChangeActions = slice.actions;
export const emailChangeReducer = slice.reducer;
